import { useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import {
  CalendarDays,
  CalendarCheck,
  MessageCircle,
  Mail,
  ChevronDown,
  ArrowRight,
  Scissors,
  UtensilsCrossed,
  Dumbbell,
  Sparkles,
  BriefcaseBusiness,
  Flower2,
} from 'lucide-react'
import { BookingRainBackground } from '../components/BookingRainBackground'
import { MarketingNav } from '../components/MarketingNav'
import { MarketingFooter } from '../components/MarketingFooter'
import { useWindowWidth } from '../hooks/useWindowWidth'

// ─── Shared types ────────────────────────────────────────────────────────────

interface FadeInProps {
  children: React.ReactNode
  delay?: number
  className?: string
  style?: React.CSSProperties
}

// ─── FadeIn helper ───────────────────────────────────────────────────────────

function FadeIn({ children, delay = 0, className, style }: FadeInProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      ref={ref}
      initial={reduceMotion ? {} : { opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroSection() {
  const isMobile = useWindowWidth() < 768

  return (
    <section style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: isMobile ? '100px 20px 60px' : '120px 32px 80px',
      zIndex: 1,
    }}>
      {/* Headline */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ maxWidth: 860 }}
      >
        {/* Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(255,109,0,0.1)',
          border: '1px solid rgba(255,109,0,0.25)',
          borderRadius: 100,
          padding: '6px 16px',
          marginBottom: 32,
        }}>
          <Sparkles size={14} color="#FF6D00" />
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12, fontWeight: 600,
            color: '#FF9A00',
            letterSpacing: '0.04em',
          }}>
            Buchungsautomatisierung für lokale Businesses
          </span>
        </div>

        <h1 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontWeight: 800,
          fontSize: 'clamp(48px, 8vw, 82px)',
          lineHeight: 1.03,
          letterSpacing: '-0.025em',
          margin: 0,
        }}>
          <span style={{ color: 'white', display: 'block' }}>Alle Buchungen.</span>
          <span style={{
            display: 'block',
            background: 'linear-gradient(135deg, #FF6D00 0%, #FF9A00 50%, #FFB800 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Automatisch.
          </span>
        </h1>

        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: isMobile ? 16 : 19,
          lineHeight: 1.65,
          color: 'rgba(232,234,246,0.6)',
          maxWidth: 560,
          margin: '24px auto 0',
          fontWeight: 400,
        }}>
          Smarte Terminverwaltung für jedes lokale Business —
          vom Friseursalon bis zum Fitnessstudio.
        </p>

        {/* CTA Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 16,
          justifyContent: 'center',
          marginTop: 44,
        }}>
          <motion.a
            href="#start"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 16, fontWeight: 700,
              color: 'white',
              textDecoration: 'none',
              padding: '14px 32px',
              borderRadius: 14,
              background: '#FF6D00',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: '0 8px 32px rgba(255,109,0,0.35), 0 2px 8px rgba(255,109,0,0.2)',
            }}
          >
            Kostenlos starten <ArrowRight size={18} />
          </motion.a>
          <motion.a
            href="#demo"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 16, fontWeight: 700,
              color: '#00B0FF',
              textDecoration: 'none',
              padding: '14px 32px',
              borderRadius: 14,
              border: '1px solid rgba(0,176,255,0.3)',
              background: 'rgba(0,176,255,0.06)',
              backdropFilter: 'blur(8px)',
            }}
          >
            Demo ansehen
          </motion.a>
        </div>
      </motion.div>

      {/* Chat Widget Preview — floats bottom-right, hidden on mobile */}
      <motion.div
        initial={{ opacity: 0, x: 40, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.8, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          display: isMobile ? 'none' : 'block',
          position: 'absolute',
          right: '6%',
          bottom: '10%',
          width: 240,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(0,176,255,0.2)',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Widget header */}
        <div style={{
          background: '#FF6D00',
          padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{
            width: 28, height: 28,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageCircle size={14} color="white" />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'white', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Candlear
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, background: '#4ADE80', borderRadius: '50%', display: 'inline-block' }} />
              Online
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{
          background: 'rgba(14,11,30,0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
        }}>
          {[
            { label: 'Chat', active: true },
            { label: 'Termin buchen', active: false },
          ].map(({ label, active }) => (
            <div
              key={label}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '7px 4px 6px',
                fontSize: 10,
                fontWeight: 700,
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                color: active ? '#FF6D00' : 'rgba(232,234,246,0.35)',
                borderBottom: active ? '2px solid #FF6D00' : '2px solid transparent',
                letterSpacing: '0.01em',
              }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Chat messages */}
        <div style={{ padding: '12px 10px', background: 'rgba(14,11,30,0.8)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '12px 12px 12px 4px',
            padding: '8px 10px',
            maxWidth: '80%',
          }}>
            <p style={{ fontSize: 11, color: 'rgba(232,234,246,0.85)', margin: 0, lineHeight: 1.4 }}>
              Hallo! Wann soll dein Termin sein?
            </p>
          </div>
          <div style={{
            background: '#FF6D00',
            borderRadius: '12px 12px 4px 12px',
            padding: '8px 10px',
            maxWidth: '80%',
            alignSelf: 'flex-end',
          }}>
            <p style={{ fontSize: 11, color: 'white', margin: 0, lineHeight: 1.4 }}>
              Morgen, 10 Uhr bitte
            </p>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '12px 12px 12px 4px',
            padding: '8px 10px',
            maxWidth: '90%',
          }}>
            <p style={{ fontSize: 11, color: 'rgba(232,234,246,0.85)', margin: 0, lineHeight: 1.4 }}>
              ✓ Termin gebucht! Bestätigung kommt per Email.
            </p>
          </div>
        </div>

        {/* Input bar */}
        <div style={{
          background: 'rgba(14,11,30,0.9)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          padding: '8px 10px',
          display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <div style={{
            flex: 1, background: 'rgba(255,255,255,0.06)',
            borderRadius: 20, padding: '5px 10px',
            fontSize: 10, color: 'rgba(232,234,246,0.3)',
          }}>
            Nachricht...
          </div>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: '#FF6D00',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ArrowRight size={12} color="white" />
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          color: 'rgba(232,234,246,0.25)',
        }}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
      >
        <span style={{ fontSize: 10, fontFamily: 'Inter, sans-serif', fontWeight: 500, letterSpacing: '0.08em' }}>
          SCROLL
        </span>
        <ChevronDown size={16} />
      </motion.div>
    </section>
  )
}

// ─── Marquee Strip ────────────────────────────────────────────────────────────

const INDUSTRIES = [
  { icon: Scissors,          label: 'Friseur' },
  { icon: UtensilsCrossed,   label: 'Restaurant' },
  { icon: Dumbbell,          label: 'Fitness' },
  { icon: Sparkles,          label: 'Kosmetik' },
  { icon: BriefcaseBusiness, label: 'Coaching' },
  { icon: Flower2,           label: 'Yoga' },
]

function MarqueeStrip() {
  const items = [...INDUSTRIES, ...INDUSTRIES] // double for seamless loop

  return (
    <div style={{
      position: 'relative',
      zIndex: 1,
      background: 'rgba(17,15,32,0.9)',
      borderTop: '1px solid rgba(101,79,111,0.18)',
      borderBottom: '1px solid rgba(101,79,111,0.18)',
      overflow: 'hidden',
      height: 72,
      display: 'flex',
      alignItems: 'center',
    }}>
      {/* Left / right fade */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 120, zIndex: 2,
        background: 'linear-gradient(to right, rgba(17,15,32,1), transparent)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 120, zIndex: 2,
        background: 'linear-gradient(to left, rgba(17,15,32,1), transparent)',
        pointerEvents: 'none',
      }} />

      <motion.div
        style={{ display: 'flex', gap: 0, whiteSpace: 'nowrap' }}
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 36px',
              color: 'rgba(232,234,246,0.4)',
            }}
          >
            <item.icon size={15} />
            <span style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 13, fontWeight: 600,
              letterSpacing: '0.03em',
            }}>
              {item.label}
            </span>
            <span style={{
              width: 4, height: 4, borderRadius: '50%',
              background: 'rgba(255,109,0,0.5)',
              display: 'inline-block',
              marginLeft: 20,
            }} />
          </div>
        ))}
      </motion.div>
    </div>
  )
}

// ─── Features ─────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: CalendarDays,
    title: 'Smarte Verfügbarkeit',
    description: 'Buchungsregeln, Ressourcen und Zeitpuffer — alles automatisch. Kein manuelles Verwalten mehr.',
    glow: '#00B0FF',
    delay: 0,
  },
  {
    icon: MessageCircle,
    title: 'Chat-Widget',
    description: 'Kunden buchen direkt per Chat-Bot auf deiner Website. Kein App-Download, kein Account nötig.',
    glow: '#FF6D00',
    delay: 0.14,
  },
  {
    icon: Mail,
    title: 'Email-Bestätigung',
    description: '6-stelliger Bestätigungscode — automatisch. Kunden-Account ist nicht erforderlich.',
    glow: '#654F6F',
    delay: 0.28,
  },
  {
    icon: CalendarCheck,
    title: 'Direktbuchung im Widget',
    description: 'Kunden wählen Datum, Zeit und Termin direkt im Widget — ohne Chat. Drei Schritte, E-Mail-Bestätigung, fertig.',
    glow: '#4CAF50',
    delay: 0.42,
  },
]

function FeaturesSection() {
  const isMobile = useWindowWidth() < 768

  return (
    <section id="features" style={{
      position: 'relative',
      zIndex: 1,
      background: 'rgba(18,15,34,0.96)',
      padding: isMobile ? '64px 20px' : '100px 32px',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeIn style={{ textAlign: 'center', marginBottom: 64 }}>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: '#FF6D00', marginBottom: 12,
          }}>
            Was du bekommst
          </p>
          <h2 style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 'clamp(32px, 5vw, 46px)',
            fontWeight: 700,
            color: '#E8EAF6',
            margin: 0,
            letterSpacing: '-0.02em',
          }}>
            Alles was du brauchst.
            <br />
            <span style={{ color: 'rgba(232,234,246,0.45)', fontWeight: 400 }}>Nichts was du nicht brauchst.</span>
          </h2>
        </FadeIn>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 24,
        }}>
          {FEATURES.slice(0, 3).map((feature) => (
            <FadeIn key={feature.title} delay={feature.delay}>
              <FeatureCard feature={feature} />
            </FadeIn>
          ))}
        </div>
        {FEATURES.length > 3 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 24,
          }}>
            <div style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}>
              <FadeIn delay={FEATURES[3].delay}>
                <FeatureCard feature={FEATURES[3]} />
              </FadeIn>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function FeatureCard({ feature }: { feature: typeof FEATURES[0] }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${hovered ? `${feature.glow}50` : 'rgba(101,79,111,0.28)'}`,
        borderRadius: 20,
        padding: 32,
        overflow: 'hidden',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 16px 48px rgba(0,0,0,0.3), 0 0 0 1px ${feature.glow}20`
          : '0 2px 16px rgba(0,0,0,0.15)',
        transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Corner glow */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0,
        width: 200, height: 200,
        background: `radial-gradient(circle at 0% 0%, ${feature.glow}18 0%, transparent 65%)`,
        pointerEvents: 'none',
        opacity: hovered ? 1 : 0.6,
        transition: 'opacity 200ms ease',
      }} />

      {/* Icon */}
      <div style={{
        width: 48, height: 48,
        borderRadius: 14,
        background: `${feature.glow}18`,
        border: `1px solid ${feature.glow}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
        position: 'relative',
      }}>
        <feature.icon size={22} color={feature.glow} />
      </div>

      <h3 style={{
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        fontSize: 18, fontWeight: 700,
        color: 'white', margin: '0 0 10px',
        position: 'relative',
      }}>
        {feature.title}
      </h3>
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 14, lineHeight: 1.65,
        color: 'rgba(232,234,246,0.55)',
        margin: 0,
        position: 'relative',
      }}>
        {feature.description}
      </p>
    </div>
  )
}

// ─── How It Works ─────────────────────────────────────────────────────────────

const STEPS = [
  {
    number: '01',
    title: 'Setup in 5 Minuten',
    description: 'Admin Dashboard öffnen, Öffnungszeiten und Ressourcen konfigurieren. Fertig.',
    color: '#FF6D00',
  },
  {
    number: '02',
    title: 'Widget einbetten',
    description: 'Ein <script>-Tag auf deine Website kopieren. Kein Code-Wissen nötig.',
    color: '#00B0FF',
  },
  {
    number: '03',
    title: 'Buchungen laufen',
    description: 'Kunden buchen rund um die Uhr. Email-Bestätigung kommt automatisch.',
    color: '#4CAF50',
  },
]

function HowItWorksSection() {
  const lineRef = useRef<SVGPathElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const inView = useInView(sectionRef, { once: true, margin: '-100px' })
  const reduceMotion = useReducedMotion()
  const isMobile = useWindowWidth() < 768

  return (
    <section id="demo" ref={sectionRef} style={{
      position: 'relative',
      zIndex: 1,
      background: '#0E0B1E',
      padding: isMobile ? '64px 20px' : '100px 32px',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeIn style={{ textAlign: 'center', marginBottom: 72 }}>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: '#00B0FF', marginBottom: 12,
          }}>
            So einfach geht's
          </p>
          <h2 style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 'clamp(32px, 5vw, 46px)',
            fontWeight: 700,
            color: '#E8EAF6',
            margin: 0,
            letterSpacing: '-0.02em',
          }}>
            In 3 Schritten live.
          </h2>
        </FadeIn>

        {/* Steps */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 0,
          position: 'relative',
        }}>
          {/* Connecting SVG line — desktop only */}
          {!isMobile && (
            <svg
              style={{ position: 'absolute', top: 40, left: 0, width: '100%', height: 2, overflow: 'visible', pointerEvents: 'none' }}
              viewBox="0 0 1000 2"
              preserveAspectRatio="none"
            >
              <motion.path
                ref={lineRef}
                d="M 150 1 L 850 1"
                stroke="rgba(101,79,111,0.4)"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
                transition={{ duration: reduceMotion ? 0 : 1.2, delay: 0.3, ease: 'easeOut' }}
              />
            </svg>
          )}

          {STEPS.map((step, i) => (
            <FadeIn key={step.number} delay={i * 0.18}>
              <div style={{
                padding: isMobile ? '0 0 40px' : '0 32px',
                textAlign: 'center',
                position: 'relative',
              }}>
                {/* Number badge */}
                <div style={{
                  width: 56, height: 56,
                  borderRadius: '50%',
                  background: `${step.color}18`,
                  border: `2px solid ${step.color}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 24px',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  <span style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: 16, fontWeight: 800,
                    color: step.color,
                  }}>
                    {step.number}
                  </span>
                </div>

                <h3 style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: 18, fontWeight: 700,
                  color: 'white',
                  margin: '0 0 12px',
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14, lineHeight: 1.65,
                  color: 'rgba(232,234,246,0.5)',
                  margin: 0,
                }}>
                  {step.description}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── CTA + Footer ─────────────────────────────────────────────────────────────

function CtaSection() {
  const isMobile = useWindowWidth() < 768

  return (
    <section id="start" style={{
      position: 'relative',
      zIndex: 1,
      background: 'linear-gradient(to bottom, rgba(18,15,34,0.98), rgba(26,16,64,0.98))',
      padding: isMobile ? '80px 20px 0' : '120px 32px 0',
      borderTop: '1px solid rgba(101,79,111,0.18)',
    }}>
      <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <FadeIn>
          <h2 style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 'clamp(36px, 6vw, 56px)',
            fontWeight: 800,
            color: 'white',
            margin: '0 0 20px',
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
          }}>
            Dein Business.
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #FF6D00, #FFB800)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Immer buchbar.
            </span>
          </h2>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 18, lineHeight: 1.65,
            color: 'rgba(232,234,246,0.55)',
            marginBottom: 48,
          }}>
            Starte jetzt — kostenlos, kein Setup-Aufwand.
          </p>

          <PulsingCtaButton />
        </FadeIn>
      </div>

      <MarketingFooter />
    </section>
  )
}

function PulsingCtaButton() {
  const isMobile = useWindowWidth() < 768

  return (
    <motion.a
      href="/admin"
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      animate={{
        boxShadow: [
          '0 8px 32px rgba(255,109,0,0.3), 0 2px 8px rgba(255,109,0,0.2)',
          '0 8px 48px rgba(255,109,0,0.55), 0 2px 16px rgba(255,109,0,0.3)',
          '0 8px 32px rgba(255,109,0,0.3), 0 2px 8px rgba(255,109,0,0.2)',
        ],
      }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        fontSize: isMobile ? 16 : 18,
        fontWeight: 700,
        color: 'white',
        textDecoration: 'none',
        padding: isMobile ? '16px 32px' : '18px 44px',
        borderRadius: 16,
        background: '#FF6D00',
      }}
    >
      Jetzt kostenlos starten <ArrowRight size={isMobile ? 18 : 20} />
    </motion.a>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div style={{ position: 'relative', background: '#0E0B1E', minHeight: '100vh', overflowX: 'hidden' }}>
      <BookingRainBackground />
      <MarketingNav />
      <HeroSection />
      <MarqueeStrip />
      <FeaturesSection />
      <HowItWorksSection />
      <CtaSection />
    </div>
  )
}
